// components/ProofUploadModal.js - Clean professional design matching reference
import React, { useState } from 'react';
import { Upload, X, Camera, AlertCircle, CheckCircle } from 'lucide-react';

const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';


export const ProofUploadModal = ({ 
  isOpen, 
  onClose, 
  deposit, 
  guardian, 
  onProofSubmitted 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !deposit) return null;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const uploadToBackend = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const uploadResponse = await fetch(`${API_BASE_URL}/upload/proof/${deposit.id}`, {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json();
      throw new Error(errorData.error || 'Upload failed');
    }

    const uploadResult = await uploadResponse.json();
    if (!uploadResult.success) {
      throw new Error(uploadResult.error || 'Upload failed');
    }

    return uploadResult.data.url;
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select an image file');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const imageUrl = await uploadToBackend(selectedFile);
      
      const response = await fetch(`${API_BASE_URL}/deposits/${deposit.id}/proof`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guardian_id: guardian.id,
          proof_image_url: imageUrl
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit proof');
      }

      const result = await response.json();
      if (result.success) {
        onProofSubmitted(result.data);
        onClose();
      }
    } catch (error) {
      setError(error.message || 'Failed to submit proof');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setError('');
      onClose();
    }
  };

  // Updated ProofUploadModal with proper scrolling
return (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
      
      {/* Header - Fixed */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
            <Camera className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Upload Deposit Proof</h3>
            <p className="text-sm text-gray-500">Confirm this money was deposited into an account</p>
          </div>
        </div>
        <button 
          onClick={handleClose}
          disabled={isUploading}
          className="text-gray-400 hover:text-gray-600 p-1"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Deposit Info */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold text-emerald-800">{deposit.first_name}</h4>
                <p className="text-emerald-600 text-sm">{deposit.description || 'Allowance'}</p>
                <p className="text-emerald-500 text-xs">
                  Requested {new Date(deposit.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-700">${deposit.amount}</div>
                <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                  Awaiting Proof
                </div>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-yellow-500 rounded-lg flex items-center justify-center mt-0.5">
                <AlertCircle className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="font-medium text-yellow-800 text-sm mb-2">Required Proof Documentation</h5>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="font-medium text-yellow-700 mb-1">✅ Acceptable Documents:</p>
                    <ul className="text-yellow-600 space-y-0.5 text-xs">
                      <li>• Bank deposit receipt</li>
                      <li>• ATM deposit confirmation</li>
                      <li>• Mobile banking screenshot</li>
                      <li>• Bank statement entry</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-yellow-700 mb-1">📋 Must Show:</p>
                    <ul className="text-yellow-600 space-y-0.5 text-xs">
                      <li>• Exact amount: ${deposit.amount}</li>
                      <li>• Date of deposit</li>
                      <li>• Bank/account information</li>
                      <li>• Clear, legible text</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* File Upload */}
          {!previewUrl ? (
            <label className={`
              block border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer
              hover:border-emerald-400 hover:bg-emerald-50 transition-colors
              ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h4 className="text-lg font-medium text-gray-700 mb-1">Choose Proof Image</h4>
              <p className="text-gray-500 text-sm mb-2">Click to select or drag and drop your deposit proof</p>
              <p className="text-gray-400 text-xs">JPG, PNG, HEIC up to 5MB</p>
            </label>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Deposit proof"
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setError('');
                  }}
                  disabled={isUploading}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm hover:bg-gray-50"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <div className="flex-1">
                    <p className="text-emerald-800 font-medium text-sm">{selectedFile?.name}</p>
                    <p className="text-emerald-600 text-xs">
                      {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB • Ready to upload
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* What happens next info */}
          <div className="bg-emerald-50 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center mt-0.5">
                <CheckCircle className="w-3 h-3 text-white" />
              </div>
              <div>
                <h6 className="font-medium text-emerald-800 text-sm mb-1">What happens next?</h6>
                <div className="text-emerald-700 text-xs space-y-0.5">
                  <p>1. Your image will be securely stored and encrypted</p>
                  <p>2. Our admin team will review your proof within 24 hours</p>
                  <p>3. Once approved, the money will be added to {deposit.first_name}'s account</p>
                  <p>4. You'll receive a confirmation notification</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Fixed */}
      <div className="bg-gray-50 p-6 border-t border-gray-100 flex-shrink-0">
        <div className="flex space-x-3">
          <button
            onClick={handleSubmit}
            disabled={!selectedFile || isUploading}
            className={`
              flex-1 py-3 px-4 rounded-xl font-medium transition-all
              ${!selectedFile || isUploading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
              }
            `}
          >
            {isUploading ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Uploading...
              </div>
            ) : (
              'Submit Proof for Admin Review'
            )}
          </button>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  );
};